import { useEffect, useState } from "react";
import { Smartphone, Share2, Plus, X } from "lucide-react";

/**
 * Cross-platform PWA install banner.
 *
 * - Android / Chrome: uses native `beforeinstallprompt` event
 * - iOS Safari: shows manual instructions (no native API)
 * - Already installed (standalone): nothing shown
 */
export default function PWAInstallBanner({ studioName, primaryColor = "#F77F2A" }) {
  const [installEvent, setInstallEvent] = useState(null);
  const [iosOpen, setIosOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const ua = window.navigator.userAgent || "";
    const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    setIsIos(ios);

    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("gs-install-dismissed") === "1") setDismissed(true);
  }, []);

  const close = () => {
    setDismissed(true);
    sessionStorage.setItem("gs-install-dismissed", "1");
  };

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === "accepted") {
      setInstallEvent(null);
    }
    close();
  };

  // Hide if already installed or dismissed
  if (isStandalone || dismissed) return null;

  // iOS: show banner + on-tap instructions
  if (isIos) {
    return (
      <>
        <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3" data-testid="pwa-install-banner-ios">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: primaryColor + "20" }}>
            <Smartphone className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-black text-[#1E293B] truncate">Installa l&apos;app sul telefono</div>
            <div className="text-xs text-slate-500 truncate">Più veloce, sempre a portata di mano</div>
          </div>
          <button onClick={() => setIosOpen(true)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white shrink-0" style={{ background: primaryColor }} data-testid="pwa-install-btn">
            Installa
          </button>
          <button onClick={close} className="p-1 text-slate-400 hover:text-slate-600 shrink-0" aria-label="Chiudi">
            <X className="w-4 h-4" />
          </button>
        </div>

        {iosOpen && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setIosOpen(false)}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl max-w-sm w-full p-6 shadow-2xl">
              <h3 className="text-xl font-black text-[#1E293B] mb-4">Installa {studioName || "GoStreet"}</h3>
              <ol className="space-y-4 text-sm text-slate-700">
                <li className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-slate-100 font-black text-slate-700 flex items-center justify-center shrink-0">1</div>
                  <div className="pt-1">
                    Tocca l&apos;icona <Share2 className="inline w-4 h-4 text-blue-500 mx-1" /> <strong>Condividi</strong> in basso al centro del browser
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-slate-100 font-black text-slate-700 flex items-center justify-center shrink-0">2</div>
                  <div className="pt-1">
                    Scorri in basso e tocca <strong>«Aggiungi alla schermata Home»</strong> <Plus className="inline w-4 h-4 ml-1" />
                  </div>
                </li>
                <li className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-slate-100 font-black text-slate-700 flex items-center justify-center shrink-0">3</div>
                  <div className="pt-1">Tocca <strong>«Aggiungi»</strong> in alto a destra</div>
                </li>
              </ol>
              <button onClick={() => setIosOpen(false)} className="mt-6 w-full py-3 rounded-xl font-bold text-white" style={{ background: primaryColor }}>
                Ho capito
              </button>
            </div>
          </div>
        )}
      </>
    );
  }

  // Android / Chrome — only if install event available
  if (!installEvent) return null;

  return (
    <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3" data-testid="pwa-install-banner-android">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: primaryColor + "20" }}>
        <Smartphone className="w-5 h-5" style={{ color: primaryColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black text-[#1E293B] truncate">Installa l&apos;app sul telefono</div>
        <div className="text-xs text-slate-500 truncate">Apri al volo dopo un sinistro</div>
      </div>
      <button onClick={install} className="px-3 py-1.5 rounded-lg text-xs font-bold text-white shrink-0" style={{ background: primaryColor }} data-testid="pwa-install-btn">
        Installa
      </button>
      <button onClick={close} className="p-1 text-slate-400 hover:text-slate-600 shrink-0" aria-label="Chiudi">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
